import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { Search, ClipboardList, Code, CheckCircle, Rocket } from 'lucide-react';
import { IconCard } from '../IconCard';
import { isFinnishPage } from '../../../lib/blogPaths';

const stepsEn = [
  { icon: Search, title: 'Discovery', description: 'We listen first—your goals, constraints, and how your team works.' },
  { icon: ClipboardList, title: 'Planning', description: 'Clear scope and milestones, aligned with how you operate.' },
  { icon: Code, title: 'Development', description: 'Iterative delivery with regular touchpoints and modern tooling.' },
  { icon: CheckCircle, title: 'Testing & QA', description: 'Rigorous testing and feedback loops before anything ships.' },
  { icon: Rocket, title: 'Launch & Support', description: 'Deploy with confidence, with support when you need it.' },
];

const stepsFi = [
  { icon: Search, title: 'Kartoitus', description: 'Kuuntelemme ensin—tavoitteet, rajoitteet ja miten tiimisi toimii.' },
  { icon: ClipboardList, title: 'Suunnittelu', description: 'Selkeä laajuus ja virstanpylväät, linjassa toimintatapojenne kanssa.' },
  { icon: Code, title: 'Kehitys', description: 'Iteratiivinen toimitus säännöllisillä tapaamisilla ja moderneilla työkaluilla.' },
  { icon: CheckCircle, title: 'Testaus ja laadunvarmistus', description: 'Perusteellinen testaus ja palautesilmukat ennen julkaisua.' },
  { icon: Rocket, title: 'Julkaisu ja tuki', description: 'Julkaise luottavaisin mielin, tuki tarpeen vaatiessa.' },
];

export function ServicesProcessSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const steps = isFinnish ? stepsFi : stepsEn;
  const heading = isFinnish ? 'Lähestymistapamme kartoituksesta toimitukseen' : 'Our Approach: From Discovery to Delivery';

  return (
    <section ref={ref} className="section-rhythm bg-[#f9fafb]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="heading-2 mb-4">{heading}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="relative"
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+28px)] w-[calc(100%-56px)] h-0.5 bg-gray-200 -z-0" />
                )}
                <div className="relative z-10 bg-white rounded-2xl border border-gray-200 p-6 h-full hover:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)] transition-shadow group">
                  <IconCard Icon={Icon} className="mb-4" />
                  <h3 className="heading-4 mb-2">{step.title}</h3>
                  <p className="text-sm text-[#6b7280] leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
